import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Seat from '../shared/Seat';
import HeroSeat from '../shared/HeroSeat';
import BottomControls from '../../layout/BottomControls/BottomControls';
import Question from '../../layout/BottomControls/Components/QuestionText/QuestionText';
import useHandGenerator, { useBlastHandGenerator } from '../../../components/HandGenerator';
import { useGame } from '../../../context/GameContext';
import { BET_TYPES } from '../types';
import { QuizResult } from '../../../analytics/QuizAnalytics';

import './BlastTable.css';

const POSITIONS = ['BTN', 'SB', 'BB', 'LJ', 'HJ', 'CO'];
const STARTING_STACK = 100;
const POSITION_ORDER = {
  'LJ': 0,
  'HJ': 1,
  'CO': 2,
  'BTN': 3,
  'SB': 4,
  'BB': 5
};

const isInPosition = (pos1, pos2) => {
  const positionOrder = {
    'LJ': 0,
    'HJ': 1,
    'CO': 2,
    'BTN': 3,
    'SB': 4,
    'BB': 5
  };

  if (!pos1 || !pos2 || pos1 === pos2) {
    return false;
  }

  const pos1Value = positionOrder[pos1];
  const pos2Value = positionOrder[pos2];

  return pos1Value > pos2Value;
};

const validateGameConfig = (config) => {
  if (!config) return false;

  // Blast configs use different fields — bypass cash-game validation.
  // A Blast config is identified by quizType === 'BLAST' or the presence of
  // the Blast-specific 'format' field ('3way' | 'hu').
  if (config.quizType === 'BLAST' || config.format === '3way' || config.format === 'hu') {
    const ok = !!(config.heroPosition && config.totalHands);
    if (!ok) console.error('Blast config missing heroPosition or totalHands:', config);
    return ok;
  }

  const baseRequiredFields = ['heroPosition', 'betType', 'totalHands'];
  let additionalFields = [];

  Object.keys(config).forEach(key => {
    if (config[key] === '') {
      config[key] = null;
    }
  });

  switch (config.betType) {
    case BET_TYPES.OPEN:
      break;
    case BET_TYPES.FACING_OPEN_CALL:
    case BET_TYPES.FACING_OPEN_3BET:
      additionalFields.push('openPosition');
      break;
    case BET_TYPES.FACING_3BET_CALL:
      additionalFields.push('openPosition', 'threeBetPosition');
      break;
    case BET_TYPES.FACING_3BET_4BET:
      additionalFields.push('threeBetPosition');
      break;
    default:
      console.warn('Unknown bet type:', config.betType);
  }

  const requiredFields = [...baseRequiredFields, ...additionalFields];
  const missingFields = requiredFields.filter(field => config[field] === undefined || config[field] === null);

  if (missingFields.length > 0) {
    console.error('Missing required fields in game config:', missingFields);
    return false;
  }

  switch (config.betType) {
    case BET_TYPES.OPEN:
      if (config.openPosition && config.openPosition !== config.heroPosition) {
        return false;
      }
      break;
    case BET_TYPES.FACING_OPEN_CALL:
      if (config.heroPosition === config.openPosition) return false;
      break;
    case BET_TYPES.FACING_OPEN_3BET:
      if (config.heroPosition === config.openPosition) return false;
      break;
    case BET_TYPES.FACING_3BET_CALL:
      if (config.heroPosition !== config.openPosition) {
        config.openPosition = config.heroPosition;
      }
      if (config.heroPosition === config.threeBetPosition) return false;
      break;
    case BET_TYPES.FACING_3BET_4BET:
      if (config.heroPosition === config.threeBetPosition) return false;
      if (!config.openPosition) {
        config.openPosition = config.heroPosition;
      } else if (config.openPosition === config.threeBetPosition) {
        return false;
      }
      break;
    default:
      console.warn('Unknown bet type for position validation:', config.betType);
  }

  return true;
};

const get3BetSize = (threeBetPosition, openPosition) => {
  if (!threeBetPosition) return 10;
  if (!openPosition) {
    if (threeBetPosition === 'BTN' || threeBetPosition === 'SB') return 7.5;
    return 10;
  }
  if (threeBetPosition === openPosition) return 10;
  const is3BettorInPosition = isInPosition(threeBetPosition, openPosition);
  return is3BettorInPosition ? 7.5 : 10;
};

const checkPositionRelationship = (position1, position2) => {
  const positionOrder = {
    'LJ': 0, 'HJ': 1, 'CO': 2, 'BTN': 3, 'SB': 4, 'BB': 5
  };
  const pos1Value = positionOrder[position1];
  const pos2Value = positionOrder[position2];
  const isPos1InPosition = pos1Value > pos2Value;
  return {
    isPos1InPosition,
    isPos2InPosition: !isPos1InPosition,
    pos1ActsFirst: pos1Value < pos2Value
  };
};

/**
 * Blast-specific seat calculator.
 *
 * Only renders the opponent seats that actually exist in the Blast format:
 *   3-way → BTN, SB, BB (2 opponents)
 *   HU    → SB, BB       (1 opponent)
 *
 * Seat CSS class mapping (matches BlastTable.css positions):
 *   seat-2  → left  side (SB or first opponent)
 *   seat-3  → top center (used for the single HU opponent)
 *   seat-4  → right side (BB or second opponent)
 */
const calculateSeats = (gameConfig) => {
  if (!gameConfig?.heroPosition) return [];

  const { heroPosition, format } = gameConfig;

  // ── Blast formats ─────────────────────────────────────────────────────────
  const isBlast = gameConfig.quizType === 'BLAST' ||
                  format === '3way' || format === 'hu';

  if (isBlast) {
    const startingStack = gameConfig.stack || 15;
    const blastPositions = format === 'hu' ? ['SB', 'BB'] : ['BTN', 'SB', 'BB'];
    const opponents = blastPositions.filter(p => p !== heroPosition);

    // Base blind bets — for BB-hero scenarios, show the SB's actual committed amount
    const getBlindBet = (pos) => {
      if (pos === 'BB') {
        const { action, villainPos, villainAction, heroPosition, primaryAction } = gameConfig;
        const va = (villainAction || '').toLowerCase();
        // BB jammed over SB's/BTN's raise or limp — show BB all-in
        // Covers both call (call-decision) and jam (SB limp-jam scenario)
        if ((heroPosition === 'SB' || heroPosition === 'BTN') && (action === 'call' || action === 'jam') && villainPos === 'BB' && va === 'jam') {
          return startingStack;
        }
        // BB 3bet over SB's/BTN's raise — show approximate 3bet amount
        if ((heroPosition === 'SB' || heroPosition === 'BTN') && (action === 'call' || action === 'jam') && villainPos === 'BB' && va.startsWith('3bet')) {
          return va === '3bet large' ? Math.min(8, startingStack - 1) : Math.min(6, startingStack - 1);
        }
        // BB raised (option raise) over SB's/BTN's limp — show approximate raise amount.
        // villainFromCsvDir() reports 'raise small' / 'raise large' for these csvDirs
        // (sb_vs_sb_call_vs_bb_raise_small / _large); 'open' is kept as a legacy fallback
        // for any caller that hasn't gone through that mapping.
        if ((heroPosition === 'SB' || heroPosition === 'BTN') && (action === 'call' || action === 'jam') && villainPos === 'BB' && primaryAction === 'open_limp' &&
            (va === 'open' || va === 'raise small' || va === 'raise large')) {
          return va === 'raise large' ? Math.min(4, startingStack - 1) : Math.min(3, startingStack - 1);
        }
        return 1;
      }
      if (pos === 'SB') {
        const { action, villainAction, villainPos, heroPosition } = gameConfig;
        const va = (villainAction || '').toLowerCase();
        // SB has completed to 1bb when BB is checking/raising (vs SB limp) or villain limped
        if (action === 'check' || action === 'raise' || va === 'limp') return 1;
        // BB is hero facing SB aggression — show the real bet, not just the 0.5bb blind.
        // IMPORTANT: only when SB is actually the villain; if BTN opened, SB folded → 0 chips.
        if (heroPosition === 'BB' && (action === 'call' || action === 'jam') && villainPos === 'SB') {
          if (va === 'open')       return 2.5;           // SB standard open raise
          if (va === 'jam')        return startingStack; // SB jammed (all-in)
          if (va === '3bet small') return 8;             // Approximate small 3-bet
          if (va === '3bet large') return 10;            // Approximate large 3-bet
        }
        // BB facing BTN aggression → SB folded, but their 0.5bb blind is still committed
        if (heroPosition === 'BB' && villainPos === 'BTN') return 0.5;
        return 0.5;
      }
      // BTN — show raise amount when BTN is the aggressor (e.g. SB Jam vs BTN Raise),
      // or their full stack when BTN jammed (e.g. SB Call vs BTN Jam)
      if (pos === 'BTN') {
        const { villainPos, villainAction } = gameConfig;
        const va = (villainAction || '').toLowerCase();
        if (villainPos === 'BTN') {
          if (va === 'jam') return startingStack; // BTN jammed all-in
          if (va === 'open' || va === 'raise') return Math.min(2.5, startingStack - 1);
        }
        return 0;
      }
      return 0;
    };

    // Mark a seat active if it already has chips committed (blind or open bet)
    const getSeatActive = (pos) => {
      const { action, villainPos } = gameConfig;
      // BB always has their blind in play
      if (pos === 'BB') return true;
      // SB is only active when they're the aggressor (villain) or when no specific villain
      // is set (e.g. hero=SB/BB open scenarios). If BTN opened and SB folded, SB is inactive.
      if (pos === 'SB') {
        if (!villainPos || villainPos === 'SB') return true;
        // BTN is the aggressor → SB folded, chips are gone
        return false;
      }
      // BTN (and any other position) is active when they're the designated villain
      if ((action === 'call' || action === 'jam') && pos === villainPos) return true;
      // BTN posted no blind, so default to inactive
      return false;
    };

    if (format === 'hu') {
      const pos = opponents[0];
      const bet = getBlindBet(pos);
      return [{
        id: 'seat-3',
        className: 'seat-3',
        title: pos,
        blinds: (startingStack - bet).toFixed(1),
        isDealer: pos === 'SB', // In HU, SB is always the dealer/button
        bet,
        isActive: getSeatActive(pos),
        gameConfig
      }];
    }

    // 3-way — clockwise seating: BTN → SB → BB
    // Hero sits at the bottom. Looking top-down, clockwise means:
    //   predecessor (counter-clockwise neighbour) → screen RIGHT = seat-4
    //   successor   (clockwise neighbour)         → screen LEFT  = seat-2
    // Example: hero=SB → predecessor=BTN (right/seat-4), successor=BB (left/seat-2)
    const CW_3WAY = ['BTN', 'SB', 'BB'];
    const heroIdx3 = CW_3WAY.indexOf(heroPosition);
    const predecessorPos = CW_3WAY[(heroIdx3 + 2) % 3]; // RIGHT = seat-4
    const successorPos   = CW_3WAY[(heroIdx3 + 1) % 3]; // LEFT  = seat-2

    return [
      { pos: successorPos,   seatNum: 2 },
      { pos: predecessorPos, seatNum: 4 }
    ].map(({ pos, seatNum }) => {
      const bet = getBlindBet(pos);
      return {
        id: `seat-${seatNum}`,
        className: `seat-${seatNum}`,
        title: pos,
        blinds: (startingStack - bet).toFixed(1),
        isDealer: pos === 'BTN',
        bet,
        isActive: getSeatActive(pos),
        gameConfig
      };
    });
  }

  // ── Cash-game fallback (original 6-max logic) ─────────────────────────────
  const { betType, openPosition, threeBetPosition } = gameConfig;
  const allPositions = ['LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
  const heroIdx = allPositions.indexOf(heroPosition);
  if (heroIdx === -1) return [];

  let orderedPositions = [];
  for (let i = 1; i <= 5; i++) {
    const posIndex = (heroIdx + i) % allPositions.length;
    orderedPositions.push(allPositions[posIndex]);
  }

  const seatPositions = [];
  for (let i = 0; i < 5; i++) {
    const position = orderedPositions[i];

    let bet = 0;
    if (position === 'BB') bet = 1;
    if (position === 'SB') bet = 0.5;

    if (position === openPosition) {
      if (position === 'SB') {
        bet = Math.max(bet, 3);
      } else {
        bet = Math.max(bet, 2.5);
      }
    }

    if (position === threeBetPosition &&
        (betType === 'FACING_3BET_4BET' || betType === 'FACING_3BET_CALL') &&
        position !== openPosition) {
      bet = get3BetSize(threeBetPosition, openPosition);
    }

    const stack = STARTING_STACK - bet;

    let isActive = false;
    if (betType === 'FACING_3BET_4BET' || betType === 'FACING_3BET_CALL') {
      isActive = position === threeBetPosition;
    } else if (betType === 'FACING_OPEN_CALL' || betType === 'FACING_OPEN_3BET') {
      isActive = position === openPosition;
    } else if (betType === 'OPEN') {
      const { pos1ActsFirst } = checkPositionRelationship(position, heroPosition);
      isActive = !pos1ActsFirst;
    }

    seatPositions.push({
      id: `seat-${i + 1}`,
      className: `seat-${i + 1}`,
      title: position,
      blinds: stack.toFixed(1),
      isDealer: position === 'BTN',
      bet,
      isActive,
      gameConfig
    });
  }

  return seatPositions;
};

const getRangeTitleText = (gameConfig) => {
  if (!gameConfig?.heroPosition) return 'Range';

  const pos = gameConfig.heroPosition;
  const isBlast = gameConfig.quizType === 'BLAST' ||
                  gameConfig.format === '3way' || gameConfig.format === 'hu';

  if (isBlast) {
    const { action, subtype, filterMode, villainPos, villainAction } = gameConfig;
    const sub = subtype || filterMode || '';

    if (action === 'open') {
      if (sub === 'raise')                    return `${pos} Open-Raising`;
      if (sub === 'jam' || sub === 'allin')  return `${pos} Open-Jamming`;
      if (sub === 'call' || sub === 'limp')   return `${pos} Limp`;
      return `${pos} Open`; // 'all' or no subtype → both raise+jam possible
    }
    if (action === 'jam') {
      if (villainPos) {
        const va = (villainAction || '').toLowerCase();
        const vaLabel = va === 'open'        ? 'Raise'
                      : va === 'raise small'  ? 'Small Raise'
                      : va === 'raise large'  ? 'Large Raise'
                      : (va.charAt(0).toUpperCase() + va.slice(1));
        return `${pos} Jam vs ${villainPos} ${vaLabel}`.trim();
      }
      return `${pos} Jam`;
    }
    if (action === 'complete') return `${pos} Complete`;
    if (action === 'check') return `${pos} Check`;
    if (action === 'raise') {
      if (sub.startsWith('raise_')) return `${pos} Raise ${sub.replace('raise_', '')}`;
      if (sub === 'allin')          return `${pos} Jam`;
      if (sub === 'all')            return `BB Option`;
      if (sub === 'raise-small')    return `${pos} Small Raise`;
      if (sub === 'raise-large')    return `${pos} Large Raise`;
      return `${pos} Raise`;
    }
    if (action === 'call') {
      const va = (villainAction || '').toLowerCase();
      // Clarify hero's own prior action (raise vs limp) so "Call vs BB 3-Bet"
      // isn't ambiguous about whether hero opened with a raise or a limp
      // before facing the villain's reaction — e.g. "SB Raise-call vs BB
      // 3-Bet" vs "SB Limp-call vs BB Open". A true 3-bet requires hero to
      // have raised first; if hero limped, villain's raise isn't a "3-bet".
      const isOpenRaise = gameConfig.primaryAction === 'open_raise';
      const isOpenLimp  = gameConfig.primaryAction === 'open_limp';
      const heroVerb = isOpenRaise ? 'Raise-call' : isOpenLimp ? 'Limp-call' : 'Call';
      if (va === 'jam') {
        // SB raised then BB jammed = 3Bet Jam; distinguish from cold jam
        return isOpenRaise
          ? `${pos} ${heroVerb} vs ${villainPos || ''} 3Bet Jam`.trim()
          : `${pos} ${heroVerb} vs ${villainPos || ''} Jam`.trim();
      }
      if (va === 'limp')             return `${pos} vs ${villainPos || ''} Limp`.trim();
      // BB never "opens" after hero has already acted (e.g. limped) — BB is
      // reacting with a sized raise. Only a true cold open (no prior hero
      // action) should ever reach the 'open' fallback below.
      if (va === 'raise small')      return `${pos} ${heroVerb} vs ${villainPos || ''} Small Raise`.trim();
      if (va === 'raise large')      return `${pos} ${heroVerb} vs ${villainPos || ''} Large Raise`.trim();
      if (va === 'open')             return `${pos} ${heroVerb} vs ${villainPos || ''} Open`.trim();
      if (va === '3bet small' || va === '3bet large') return `${pos} ${heroVerb} vs ${villainPos || ''} 3-Bet`.trim();
      return `${pos} ${heroVerb}`;
    }
    return `${pos} Blast`;
  }

  // Cash-game titles
  const { betType, openPosition, threeBetPosition, heroPosition } = gameConfig;
  if (!betType) return `${pos} Range`;

  if (betType === 'FACING_3BET_4BET')  return `${heroPosition} 4Bet vs ${threeBetPosition} 3Bet`;
  if (betType === 'FACING_3BET_CALL')  return `${heroPosition} Call vs ${threeBetPosition} 3Bet`;
  if (betType === 'FACING_OPEN_3BET')  return `${heroPosition} 3Bet vs ${openPosition} Open`;
  if (betType === 'FACING_OPEN_CALL')  return `${heroPosition} Call vs ${openPosition} Open`;
  if (betType === 'OPEN')              return `${heroPosition} Open`;
  if (betType === 'SB_LIMP' || betType === 'LIMP' ||
      (heroPosition === 'SB' && betType.toUpperCase().includes('LIMP'))) return 'SB Limp';
  return `${heroPosition} ${betType}`;
};

const RangeTitle = ({ gameConfig }) => {
  if (!gameConfig) return null;
  const stack = gameConfig.stack || 15;
  const fullTitle = getRangeTitleText(gameConfig);
  const lastSpace = fullTitle.lastIndexOf(' ');
  const titleStart = lastSpace !== -1 ? fullTitle.slice(0, lastSpace) : '';
  const lastWord = lastSpace !== -1 ? fullTitle.slice(lastSpace + 1) : fullTitle;
  return (
    <div className="range-title">
      {titleStart}{titleStart ? ' ' : ''}
      <span style={{ whiteSpace: 'nowrap' }}>{lastWord} | {stack}bb</span>
    </div>
  );
};

const getQuestionText = (gameConfig) => {
  if (!gameConfig) return "";

  // Blast questions
  if (gameConfig.quizType === 'BLAST' ||
      gameConfig.format === '3way' || gameConfig.format === 'hu') {
    // Sub-range frequency mode
    if (isFreqMode(gameConfig)) {
      const sub = gameConfig.subtype || gameConfig.filterMode || '';
      const va = (gameConfig.villainAction || '').toLowerCase();
      const stack = gameConfig.stack;
      const prefix = stack ? `On a ${stack}bb stack, ` : '';

      if (sub === 'jam' || sub === 'allin') return `${prefix}what frequency are you jamming?`;
      if (sub === 'raise_large')            return `${prefix}what frequency are you raising large?`;
      // Small raise stays plain — it reads as the standard raise, no qualifier needed.
      if (sub.startsWith('raise_'))         return `${prefix}what frequency are you raising?`;
      // Only frame this as "limping" when hero's own opening action IS the
      // limp (action === 'open'). Reaction scenarios — e.g. SB raised, faces
      // a BB 3-bet/raise/jam, and subtype lands on 'call' from the tertiary
      // filter — are a calling decision, not a limping one, even though the
      // subtype value happens to be 'call' in both cases.
      if (gameConfig.action === 'open' && (sub === 'call' || sub === 'limp')) {
        const heroPos = gameConfig.heroPosition || gameConfig.position || '';
        return heroPos === 'SB'
          ? `${prefix}what frequency are you limping?`
          : `${prefix}what frequency are you calling?`;
      }
      if (gameConfig.action === 'check') return `${prefix}what frequency are you checking?`;
      if (gameConfig.action === 'call' && va !== 'limp') {
        const sub = gameConfig.subtype || gameConfig.filterMode || '';
        if (sub === 'allin') return `${prefix}what frequency are you jamming?`;
        return `${prefix}what frequency are you calling?`;
      }
      // Reacting to villain's large raise (e.g. BB Large Raise) — flag it as
      // "raising large" per user request; villain's small raise stays as the
      // plain "raising?" phrasing since a small raise reads as the standard one.
      if (va.includes('raise large')) return `${prefix}what frequency are you raising large?`;
      return `${prefix}what frequency are you raising?`;
    }
    const stack = gameConfig.stack;
    return stack
      ? `On a ${stack}bb stack, what is the best action to take?`
      : "Based on your holding, what is the best action to take?";
  }

  // Cash-game frequency questions (unchanged)
  const { betType, openPosition, threeBetPosition, heroPosition } = gameConfig;

  if (betType === 'SB_LIMP' || betType === 'LIMP' ||
      (heroPosition === 'SB' && (betType || '').toUpperCase().includes('LIMP'))) {
    return "What frequency are you limping?";
  }

  switch (betType) {
    case BET_TYPES.OPEN:
      return "What frequency are you opening?";
    case BET_TYPES.FACING_OPEN_CALL:
      return `The ${openPosition} opened, what percent do you call with this hand?`;
    case BET_TYPES.FACING_OPEN_3BET:
      return `The ${openPosition} opened, what percent do you 3bet this hand?`;
    case BET_TYPES.FACING_3BET_CALL:
      return `The ${threeBetPosition} 3bet, what percent do you call this hand?`;
    case BET_TYPES.FACING_3BET_4BET:
      return `The ${threeBetPosition} 3bet, what percent do you 4bet this hand?`;
    default:
      return "What frequency are you playing this hand?";
  }
};

/**
 * Look up a hero hand in a Blast rangeMap and return { correctAction, frequency }.
 * rangeMap keys: 'AA', 'AKs', 'AKo'
 * The map stores entries as { action, frequency } (from useBlastHandGenerator).
 * We normalise to correctAction here so the rest of the code has a consistent shape.
 */
const lookupBlastHand = (heroCards, rangeMap) => {
  if (!heroCards || !rangeMap?.size) return { correctAction: 'fold', frequency: 0 };

  const RANKS = { A:14,K:13,Q:12,J:11,T:10,'9':9,'8':8,'7':7,'6':6,'5':5,'4':4,'3':3,'2':2 };
  const { card1Value: v1, card2Value: v2, card1Suit: s1, card2Suit: s2 } = heroCards;
  const isPair   = v1 === v2;
  const isSuited = !isPair && s1 === s2;
  const [hi, lo] = (RANKS[v1] ?? 0) >= (RANKS[v2] ?? 0) ? [v1, v2] : [v2, v1];

  const key = isPair ? hi + hi : isSuited ? hi + lo + 's' : hi + lo + 'o';
  const entry = rangeMap.get(key);
  if (!entry) return { correctAction: 'fold', frequency: 0 };

  // entry shape from useBlastHandGenerator: { action, frequency, allActions }
  // Support both { action } and { correctAction } in case shape changes later.
  const correctAction = entry.correctAction ?? entry.action ?? 'fold';
  const frequency     = entry.frequency     ?? 0;
  return { correctAction, frequency, allActions: entry.allActions };
};

/**
 * Returns true when we're in "sub-range frequency mode".
 * This applies when the user is quizzing on a specific action slice of an open
 * range (e.g. only the raise portion, or only the jam portion), so we ask
 * "how often?" instead of "what action?".
 */
const isFreqMode = (gameConfig) => {
  const sub = gameConfig?.subtype || gameConfig?.filterMode || '';
  const va  = (gameConfig?.villainAction || '').toLowerCase();
  if (gameConfig?.action === 'open' && (sub === 'raise' || sub === 'jam' || sub === 'allin' || sub === 'call' || sub === 'limp')) return true;
  if (gameConfig?.action === 'jam') return true;   // SB jam-range quiz is always frequency
  if (gameConfig?.action === 'check') return true; // BB check sub-range
  if (gameConfig?.action === 'raise') {
    if (sub === 'raise-small' || sub === 'raise-large') return true; // legacy
    if (sub.startsWith('raise_')) return true; // specific raise size (e.g. 'raise_3')
    if (sub === 'allin') return true;           // BB Option → Jam sub-range
    if (sub === 'all' || sub === 'raise-all') return false; // All Options → action mode
  }
  if (gameConfig?.action === 'call' && va !== 'limp') return true;
  return false;
};

/**
 * Determine whether a chosen action button label is correct given range data.
 *
 * Frequency-mode buttons (sub-range: raise-only or jam-only):
 *   "Raise 100%" / "Jam 100%"  → correct if frequency === 100
 *   "Raise 50%"  / "Jam 50%"   → correct if 0 < frequency < 100
 *   "Never"                     → correct if frequency === 0
 *
 * Standard binary buttons (full range):
 *   50% hands → always correct (mixed strategy — either choice is fine)
 *   100% hands → must match action exactly
 *   0% / out of range → must fold
 *
 * configAction: the quiz's action type ('open', 'check', 'jam', 'call', 'raise', …).
 *   Needed to resolve "Never" correctly in the check-quiz context, where a raise
 *   hand (never checks) is a valid "Never" answer.
 */
const isBlastAnswerCorrect = (chosenAction, rangeEntry, configAction = '', configSubtype = '', raiseSizes = []) => {
  const { correctAction, frequency, allActions } = rangeEntry;
  const isRaiseAction    = (a) => a === 'raise' || (typeof a === 'string' && a.startsWith('raise_'));
  const isCallAction     = (a) => a === 'call'  || a === 'limp';
  // CSVs may store the BB "check" (free flop) action as 'check', 'call', or 'limp'.
  const isCheckOrCall    = (a) => a === 'call'  || a === 'limp' || a === 'check';

  // For mixed-strategy hands (multiple CSV rows), compute per-action-type frequencies.
  // e.g. AJo at 25bb SB: raise 50% + check 50% → raiseFreq=50, limpFreq=50
  // Note: SB CSVs use 'check' to represent a limp (SB can't check preflop).
  // isCheckOrCall covers call/limp/check so it correctly captures SB limps stored as 'check'.
  const getActionFreq = (predicate) => {
    if (allActions && allActions.length > 1) {
      return allActions.filter(e => predicate(e.action)).reduce((sum, e) => sum + e.frequency, 0);
    }
    return predicate(correctAction) ? frequency : 0;
  };
  const raiseFreq = getActionFreq(isRaiseAction);
  const callFreq  = getActionFreq(isCheckOrCall); // includes 'check' (= SB limp in CSVs)
  const checkFreq = getActionFreq(isCheckOrCall);
  const jamFreq   = getActionFreq(a => a === 'allin');
  const totalFreq = allActions ? allActions.reduce((sum, e) => sum + e.frequency, 0) : frequency;
  const isMixed   = allActions && allActions.length > 1;

  // Frequency of this hand's raise at a specific size bucket ('large' = the
  // biggest numeric raise size present anywhere in the range, 'small' = the
  // smallest). Mirrors the sizing logic already used for the raise-large/
  // raise-small 100%/50%/0% sub-range buttons below, but generalized so the
  // plain "Large Raise" / "Small Raise" binary buttons can use it too.
  // Returns null when the range doesn't have two distinct raise sizes to
  // compare against — callers should fall back to "any raise" in that case.
  const getRaiseSizeFreq = (which) => {
    if (!Array.isArray(raiseSizes) || raiseSizes.length < 2) return null;
    const targetSize = which === 'large' ? Math.max(...raiseSizes) : Math.min(...raiseSizes);
    const actions = (allActions && allActions.length) ? allActions
      : (isRaiseAction(correctAction) ? [{ action: correctAction, frequency }] : []);
    return actions
      .filter(e => (e.action || '').startsWith('raise_') && parseFloat(e.action.split('_')[1]) === targetSize)
      .reduce((sum, e) => sum + e.frequency, 0);
  };

  // ── New Specific-mode buttons: "100%", "50%", "0%" ───────────────────────
  // Derive action family from configAction + configSubtype.
  if (chosenAction === '100%' || chosenAction === '50%' || chosenAction === '0%') {
    const isHundred = chosenAction === '100%';
    const isFifty   = chosenAction === '50%';
    const sub = configSubtype || '';

    // Jam context
    if (configAction === 'jam' || sub === 'jam' || sub === 'allin') {
      if (isHundred) return jamFreq === 100;
      if (isFifty)   return jamFreq > 0 && jamFreq < 100;
      return jamFreq === 0;
    }
    // Raise context — use raiseFreq so mixed raise+limp hands score correctly.
    // For raise-large/raise-small sub-ranges, only count the specific raise size's frequency.
    if (sub === 'raise' || sub.startsWith('raise_') || sub === 'raise-small' || sub === 'raise-large') {
      let targetFreq = raiseFreq;
      if ((sub === 'raise-large' || sub === 'raise-small') && allActions && allActions.length > 1) {
        const raiseActs = allActions
          .filter(e => { const act = e.action || ''; return act === 'raise' || act.startsWith('raise_'); })
          .sort((a, b) => {
            const sA = a.action.startsWith('raise_') ? parseFloat(a.action.split('_')[1]) : 0;
            const sB = b.action.startsWith('raise_') ? parseFloat(b.action.split('_')[1]) : 0;
            return sB - sA; // largest first
          });
        if (raiseActs.length >= 2) {
          targetFreq = sub === 'raise-large' ? raiseActs[0].frequency : raiseActs[raiseActs.length - 1].frequency;
        }
      }
      if (isHundred) return targetFreq === 100;
      if (isFifty)   return targetFreq > 0 && targetFreq < 100;
      return targetFreq === 0;
    }
    // Check context
    if (configAction === 'check') {
      if (isHundred) return checkFreq === 100;
      if (isFifty)   return checkFreq > 0 && checkFreq < 100;
      return true; // any non-check hand means "never checks" → 0% is always correct
    }
    // Call / Limp context — use callFreq so mixed raise+limp hands score correctly
    if (configAction === 'call' || sub === 'call' || sub === 'limp') {
      if (isHundred) return callFreq === 100;
      if (isFifty)   return callFreq > 0 && callFreq < 100;
      return callFreq === 0;
    }
    // Generic fallback
    if (isHundred) return totalFreq === 100;
    if (isFifty)   return totalFreq > 0 && totalFreq < 100;
    return totalFreq === 0 || !correctAction || correctAction === 'fold';
  }

  // ── Legacy frequency-mode buttons (kept for backwards compat) ────────────
  if (chosenAction === 'Raise 100%') return raiseFreq === 100;
  if (chosenAction === 'Raise 50%')  return raiseFreq > 0 && raiseFreq < 100;
  // Check buttons: accept 'check', 'call', and 'limp' as valid check actions
  // (different CSV generators use different labels for the BB free-check action).
  if (chosenAction === 'Check 100%') return checkFreq === 100;
  if (chosenAction === 'Check 50%')  return checkFreq > 0 && checkFreq < 100;
  if (chosenAction === 'Jam 100%')   return jamFreq === 100;
  if (chosenAction === 'Jam 50%')    return jamFreq > 0 && jamFreq < 100;
  if (chosenAction === 'Call 100%')  return callFreq === 100;
  if (chosenAction === 'Call 50%')   return callFreq > 0 && callFreq < 100;
  if (chosenAction === 'Never') {
    // In check/call-freq mode: hand doesn't check/call
    if (isCheckOrCall(correctAction)) return checkFreq === 0;
    // In the check-quiz context: "Never" is correct only when this hand never checks (checkFreq === 0).
    if (configAction === 'check') return checkFreq === 0;
    // In raise/jam-freq mode: hand is not in this sub-range
    return raiseFreq === 0 && jamFreq === 0 && (!correctAction || correctAction === 'fold');
  }

  // ── Standard binary buttons ───────────────────────────────────────────────
  // Mixed strategy — chosen action must be one of the actions actually in the mix.
  // (e.g. Raise/Limp 50%/50%: Raise or Limp are correct; Jam is not)
  if (frequency > 0 && frequency < 100) {
    if (allActions && allActions.length > 1) {
      if (chosenAction === 'Fold') return false;
      if (chosenAction === 'Jam') return jamFreq > 0;
      if (chosenAction === 'Raise Large' || chosenAction === 'Large Raise') {
        const sizeFreq = getRaiseSizeFreq('large');
        return sizeFreq !== null ? sizeFreq > 0 : raiseFreq > 0;
      }
      if (chosenAction === 'Raise Small' || chosenAction === 'Small Raise') {
        const sizeFreq = getRaiseSizeFreq('small');
        return sizeFreq !== null ? sizeFreq > 0 : raiseFreq > 0;
      }
      if (chosenAction === 'Raise') return raiseFreq > 0;
      if (['Call', 'Limp', 'Check', 'Complete'].includes(chosenAction)) return callFreq > 0;
      if (chosenAction.startsWith('Raise ') && !chosenAction.endsWith('%')) return raiseFreq > 0;
    }
    return true; // no allActions breakdown — treat as fully mixed (original behavior)
  }

  if (chosenAction === 'Fold')        return frequency === 0 || !correctAction || correctAction === 'fold';
  if (chosenAction === 'Raise')       return isRaiseAction(correctAction) && frequency > 0;
  if (chosenAction === 'Raise Large' || chosenAction === 'Large Raise') {
    const sizeFreq = getRaiseSizeFreq('large');
    return sizeFreq !== null ? sizeFreq > 0 : (isRaiseAction(correctAction) && frequency > 0);
  }
  if (chosenAction === 'Raise Small' || chosenAction === 'Small Raise') {
    const sizeFreq = getRaiseSizeFreq('small');
    return sizeFreq !== null ? sizeFreq > 0 : (isRaiseAction(correctAction) && frequency > 0);
  }
  if (chosenAction === 'Jam')         return correctAction === 'allin'  && frequency > 0;
  if (chosenAction === 'Call')     return isCallAction(correctAction) && frequency > 0;
  if (chosenAction === 'Limp')     return isCheckOrCall(correctAction) && frequency > 0;
  if (chosenAction === 'Check')    return (correctAction === 'check' || isCallAction(correctAction)) && frequency > 0;
  if (chosenAction === 'Complete') return isCallAction(correctAction) && frequency > 0;
  // Specific raise size (e.g. 'Raise 3', 'Raise 5.5') from BB Option → All Options mode
  if (chosenAction.startsWith('Raise ') && !chosenAction.endsWith('%')) {
    const raiseKey = `raise_${chosenAction.replace('Raise ', '')}`;
    return correctAction === raiseKey && frequency > 0;
  }
  return false;
};

const getHeroBetAndBlinds = (gameConfig) => {
  if (!gameConfig) return { bet: 0, blinds: STARTING_STACK.toFixed(1) };

  // ── Blast: use gameConfig.stack as starting stack ─────────────────────────
  const isBlast = gameConfig.quizType === 'BLAST' ||
                  gameConfig.format === '3way' || gameConfig.format === 'hu';

  if (isBlast) {
    const startingStack = gameConfig.stack || 15;
    const { heroPosition } = gameConfig;
    let heroBet = 0;
    if (heroPosition === 'BB') heroBet = 1;
    else if (heroPosition === 'SB') {
      // SB limped (completed to 1bb) before facing a villain reaction — covers both limp-call and limp-jam
      if (gameConfig.primaryAction === 'open_limp' && (gameConfig.action === 'call' || gameConfig.action === 'jam')) {
        heroBet = 1;
      } else if (gameConfig.primaryAction === 'open_raise' && (gameConfig.action === 'call' || gameConfig.action === 'jam')) {
        // SB raised 2x (2bb) and is now deciding whether to call/jam vs BB's response
        heroBet = 2;
      } else {
        heroBet = 0.5;
      }
    } else if (heroPosition === 'BTN') {
      // BTN open-raised (~2.5bb) and is now facing a villain reaction (3bet/jam),
      // deciding whether to call/jam — mirrors the raise size used for BTN-as-villain
      // in getBlindBet() above. open_jam has no follow-up decision, so heroBet stays 0.
      if (gameConfig.primaryAction === 'open_raise' && (gameConfig.action === 'call' || gameConfig.action === 'jam')) {
        heroBet = Math.min(2.5, startingStack - 1);
      }
    }
    // Pass the FULL starting stack so HeroSeat can compute remaining = stack - bet
    return { bet: heroBet, blinds: startingStack.toFixed(1) };
  }

  // ── Cash game (original logic) ────────────────────────────────────────────
  const { heroPosition, betType, openPosition } = gameConfig;
  let heroBet = 0;

  if (heroPosition === 'BB') heroBet = 1;
  if (heroPosition === 'SB') heroBet = 0.5;

  if (betType === 'OPEN') {
    if (heroPosition === 'SB') {
      heroBet = 3;
    } else {
      heroBet = 2.5;
    }
  }

  if ((betType === 'FACING_3BET_CALL' || betType === 'FACING_3BET_4BET') &&
      heroPosition === openPosition) {
    if (heroPosition === 'SB') {
      heroBet = 3;
    } else {
      heroBet = 2.5;
    }
  }

  const remainingBlinds = (STARTING_STACK - heroBet).toFixed(1);
  return { bet: heroBet, blinds: remainingBlinds };
};

/**
 * ── Decoy button injection (anti-elimination-guessing) ──────────────────────
 * A screen with only 2 real action buttons (e.g. Jam/Fold) lets players win by
 * elimination — "it's clearly not a Fold, so it must be Jam" — without ever
 * knowing the actual range. Whenever exactly 2 real buttons would be shown, a
 * third contextually-plausible decoy action is added so guessing "the one
 * that's obviously not Fold" no longer works.
 *
 * The decoy is never actually scored as correct — isBlastAnswerCorrect only
 * returns true when the chosen action matches the CSV's real solved action,
 * regardless of which buttons happen to be on screen, so a decoy naturally
 * always grades as wrong. This only needs to build a plausible label; scoring
 * correctness is untouched.
 *
 * Rules (per product decision 2026-07-09):
 *   - Only kicks in when the real button count is exactly 2 — any binary
 *     combo (Jam/Fold, Raise/Fold, Limp/Fold, Jam/Limp, …), not just Jam/Fold.
 *   - The decoy must be structurally legal for hero's position — BTN never
 *     limps or free-checks preflop in this app's model (see the isSB/BTN
 *     handling in normalizeCircleAction above), so BTN never gets a
 *     limp/check decoy. Never invent a raise-size split (Large/Small Raise)
 *     as a decoy when no real raise sizing exists at this stack — that reads
 *     as an obviously-fake option, which defeats the point.
 *   - Prefer a decoy adjacent to a real action in the Jam > Raise > Call >
 *     Fold hierarchy so it reads as plausible rather than random padding.
 *   - Which decoy gets picked is randomized across questions (when more than
 *     one adjacent candidate exists) so players can't learn "the extra
 *     button is always X."
 */
const ACTION_HIERARCHY = ['jam', 'raise', 'call', 'fold'];

// Structurally legal action buckets by hero position. BTN opens 3-way pots in
// this app's model with only Jam/Raise/Fold — it never limps or free-checks
// preflop — so a decoy must never suggest otherwise. Every other position can
// draw from the full ladder.
const getActionUniverse = (heroPos) =>
  heroPos === 'BTN' ? ['jam', 'raise', 'fold'] : ACTION_HIERARCHY;

// Collapse a button's specific PRIORITY key (jam / raise_large / raise_small /
// raise / limp / check / fold) down to its hierarchy bucket, so "already have
// a raise" is recognized regardless of sizing, and limp/check both count as
// the same "passive continue" slot.
const bucketOfKey = (key) => {
  if (key === 'jam') return 'jam';
  if (key === 'raise' || key === 'raise_large' || key === 'raise_small') return 'raise';
  if (key === 'limp' || key === 'check') return 'call';
  if (key === 'fold') return 'fold';
  return key;
};

const pickDecoyBucket = (realBuckets, heroPos, { noFold = false } = {}) => {
  const universe = getActionUniverse(heroPos).filter((k) => !(noFold && k === 'fold'));
  const candidates = universe.filter((k) => !realBuckets.includes(k));
  if (!candidates.length) return null;

  const idx = (k) => ACTION_HIERARCHY.indexOf(k);
  const adjacent = candidates.filter((c) =>
    realBuckets.some((r) => Math.abs(idx(r) - idx(c)) === 1)
  );
  const pool = adjacent.length ? adjacent : candidates;
  return pool[Math.floor(Math.random() * pool.length)];
};

/**
 * ── Fixed "opener" button sets (product decision 2026-07-10) ────────────────
 * SB Open (3-way + HU) and BB's own facing-a-limp decision ("BB Option") are
 * the two spots where hero picks their own action rather than reacting to a
 * sized bet. Unlike withDecoyIfBinary above — which injects exactly one
 * *randomized* decoy, and only when there are exactly 2 real buttons — these
 * two screens always show the same fixed action set, in the same fixed
 * order, regardless of how many actions are real at the current stack. The
 * point is that hero must weigh the Raise option on every single question,
 * not just on the stacks where it happens to be missing.
 *
 * SB Open: Jam / Raise / Limp / Fold. SB's raise is never split into
 * Large/Small at any stack, so a single 'Raise' bucket is always right here.
 */
const SB_OPENER_KEYS = ['jam', 'raise', 'limp', 'fold'];

/**
 * BB Option (facing a limp): Jam / Large Raise / Small Raise / Check — never
 * Fold, since BB can always check for free here (see noFold elsewhere in
 * this file). Most BB Option stacks solve with two distinct raise sizes; the
 * one stack that doesn't (3-way BB 7bb — see BB_OPTION_1SIZE in
 * blastRangeRules.js) falls back to a single 'Raise' bucket instead of
 * faking a Large/Small split. Inventing a size distinction the solver never
 * made would misrepresent the range AND break scoring — isBlastAnswerCorrect's
 * getRaiseSizeFreq can't tell "large" from "small" apart when raiseSizes has
 * fewer than 2 entries, so both buttons would score as correct together.
 */
const BB_OPTION_KEYS_2SIZE = ['jam', 'raise_large', 'raise_small', 'check'];
const BB_OPTION_KEYS_1SIZE = ['jam', 'raise', 'check'];

const BlastTable = () => {
  const navigate = useNavigate();
  const {
    checkAnswer,
    recordBlastAnswer,
    gameState,
    quizAnalytics,
    getNextHandDirective,
    commitHandDirective,
    markHandShown,
    markHandResult,
    shouldForceBlastLiveHand,
    markBlastHandDealt,
  } = useGame();
  // Use the Blast-specific hand generator (loads from blast preflop ranges)
  const { generateBlastHand } = useBlastHandGenerator();
  // Keep the cash-game generator available for any legacy fallback paths
  const { generateHand } = useHandGenerator();

  const [seats, setSeats] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [heroCards, setHeroCards] = useState(null);
  const [rangeMap, setRangeMap] = useState(new Map());
  const [currentQuizResult, setCurrentQuizResult] = useState(null);

  // Fixed Hands: track the randomly assigned stack for this question
  const [currentStack, setCurrentStack] = useState(null);

  const gameConfig = useMemo(() => gameState?.gameConfig, [gameState?.gameConfig]);

  // NOTE: hand-repeat tracking (and the Blast "live decision" quota) live in
  // GameContext, not local refs here — this component unmounts/remounts on
  // every question (navigates to /blast-answer and back), so a local ref
  // would lose its history each time, which is exactly why duplicate hands
  // kept showing up. See getNextHandDirective / markHandShown / markHandResult
  // / shouldForceBlastLiveHand / markBlastHandDealt from useGame().

  // Full hand-class string for a dealt hand, e.g. "AKs", "77", "94o".
  const handToClassString = useCallback((hand) => {
    if (!hand?.card1Value || !hand?.card2Value) return null;
    if (hand.card1Value === hand.card2Value) return hand.card1Value + hand.card2Value;
    const suited = hand.card1Suit === hand.card2Suit;
    return hand.card1Value + hand.card2Value + (suited ? 's' : 'o');
  }, []);

  // For Fixed Hands, inject the current question's randomly assigned stack
  const effectiveConfig = useMemo(() => {
    if (gameConfig?.isFixedHands && currentStack !== null) {
      return { ...gameConfig, stack: currentStack };
    }
    return gameConfig;
  }, [gameConfig, currentStack]);

  const rangeTitle = useMemo(() => {
    return gameState?.rangeTitle || gameConfig?.rangeTitle || "";
  }, [gameState?.rangeTitle, gameConfig?.rangeTitle]);

  useEffect(() => {
    if (!gameConfig || !validateGameConfig(gameConfig)) {
      console.error('Invalid game configuration, redirecting to blast config');
      navigate('/blast-config');
    }
  }, [gameConfig, navigate]);

  if (!gameConfig || !validateGameConfig(gameConfig)) {
    return null;
  }

  // Fixed Hands: pick a new random stack for each question
  useEffect(() => {
    if (gameConfig?.isFixedHands && gameConfig?.availableStacks?.length > 0) {
      const stacks = gameConfig.availableStacks;
      const randomStack = stacks[Math.floor(Math.random() * stacks.length)];
      setCurrentStack(randomStack);
    } else if (gameConfig?.stack) {
      setCurrentStack(gameConfig.stack);
    }
  // Re-run each question (questionCount changes) and on initial mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.questionCount, gameConfig?.isFixedHands]);

  useEffect(() => {
    let isMounted = true;

    const initializeAndLoad = async () => {
      try {
        if (!currentQuizResult && gameConfig) {
          const newQuizResult = new QuizResult(gameConfig);
          if (isMounted) {
            setCurrentQuizResult(newQuizResult);
          }
        }

        if (!gameConfig || gameState.questionCount >= gameConfig.totalHands) {
          return;
        }

        // For Fixed Hands, wait until the stack has been set for this question
        if (gameConfig.isFixedHands && currentStack === null) {
          return;
        }

        setIsProcessing(true);

        // Build effective config: for Fixed Hands, inject the current question's stack
        const effectiveConfig = gameConfig.isFixedHands
          ? { ...gameConfig, stack: currentStack }
          : gameConfig;

        // Pull a forced-return hand (a wrong answer coming due) plus the
        // current ban set, and whether this quiz's live-decision quota
        // requires this hand to come from the curated pool.
        const { forcedHandClass, usedHandKeys } = getNextHandDirective();
        const forceLive = !forcedHandClass && shouldForceBlastLiveHand(effectiveConfig.totalHands);
        const configWithKeys = { ...effectiveConfig, usedHandKeys, forcedHandClass, forceLive };
        const { hand, rangeMap: newRangeMap, wasLive } = await generateBlastHand(configWithKeys);

        if (hand && isMounted) {
          // Record this hand class so it won't be dealt again this quiz
          const handClass = handToClassString(hand);
          if (handClass) markHandShown(handClass);
          // Only now that we're committing to actually rendering this hand
          // do we remove it from the retry queue — see commitHandDirective
          // in GameContext for why this can't happen at getNextHandDirective
          // (peek) time.
          if (forcedHandClass) {
            commitHandDirective(forcedHandClass);
          }
          markBlastHandDealt(wasLive);
          setHeroCards(hand);
          if (newRangeMap) setRangeMap(newRangeMap);
          setSeats(calculateSeats(effectiveConfig));
        }
      } catch (error) {
        console.error('Error in initialization:', error);
      } finally {
        if (isMounted) {
          setIsProcessing(false);
        }
      }
    };

    initializeAndLoad();

    return () => {
      isMounted = false;
    };
  }, [
    gameConfig,
    gameState.questionCount,
    generateBlastHand,
    currentStack,
    getNextHandDirective,
    commitHandDirective,
    markHandShown,
    markBlastHandDealt,
    shouldForceBlastLiveHand,
    handToClassString,
  ]);

  useEffect(() => {
    if (!gameConfig) return;

    const newSeats = calculateSeats(gameConfig);

    if (gameConfig.threeBetPosition) {
      const threeBettorSeat = newSeats.find(seat => seat.title === gameConfig.threeBetPosition);
      if (threeBettorSeat) {
        const positionOrder = { 'LJ': 0, 'HJ': 1, 'CO': 2, 'BTN': 3, 'SB': 4, 'BB': 5 };
        const is3BettorInPosition = positionOrder[gameConfig.threeBetPosition] > positionOrder[gameConfig.openPosition];
        const expected3BetSize = is3BettorInPosition ? 7.5 : 10;

        if (threeBettorSeat.bet !== expected3BetSize) {
          threeBettorSeat.bet = expected3BetSize;
        }
      }
    }

    setSeats(newSeats);
  }, [gameConfig]);


  // ── Two-line display node for frequency/size buttons ─────────────────────
  const mkFreqNode = useCallback((action, pct) =>
    React.createElement(React.Fragment, null,
      React.createElement('span', null, action),
      React.createElement('span', { className: 'btn-pct' }, pct)
    ), []);

  // ── Derive unique raise sizes from loaded rangeMap ─────────────────────────
  // Used to label raise buttons with their actual bet size (e.g. "2.25bb").
  // Scans allActions too — mixed-strategy hands (e.g. check 50% + raise_3 50%)
  // store 'check' as the primary action, so the secondary raise size would be
  // missed if we only look at entry.action.
  // NOTE: this must be declared before handleAction below, since handleAction's
  // useCallback dependency array references raiseSizes — declaring it later
  // (even though JS hoists the `const` binding) throws a temporal-dead-zone
  // ReferenceError during render, because the deps array is evaluated
  // eagerly, not lazily inside the callback.
  const raiseSizes = useMemo(() => {
    const sizes = new Set();
    for (const entry of rangeMap.values()) {
      const actions = entry.allActions ?? [{ action: entry.action }];
      for (const { action } of actions) {
        const a = action || '';
        if (a.startsWith('raise_')) {
          const num = parseFloat(a.split('_')[1]);
          if (!isNaN(num)) sizes.add(num);
        }
      }
    }
    return [...sizes].sort((a, b) => a - b);
  }, [rangeMap]);

  // ── Blast action handler ──────────────────────────────────────────────────
  const handleAction = useCallback(async (chosenAction) => {
    if (!heroCards || !gameConfig || isProcessing) return;

    setIsProcessing(true);
    try {
      const isBlast = gameConfig.quizType === 'BLAST' ||
                      gameConfig.format === '3way' || gameConfig.format === 'hu';

      if (isBlast) {
        // Binary scoring: look up hand in loaded range
        const rangeEntry  = lookupBlastHand(heroCards, rangeMap);
        const isCorrect   = isBlastAnswerCorrect(chosenAction, rangeEntry, gameConfig.action || '', gameConfig.subtype || '', raiseSizes);
        const isLastQuestion = gameState.questionCount + 1 >= gameConfig.totalHands;

        // Wrong answers are forced back in a couple hands; correct answers
        // stay banned for the rest of the quiz (see GameContext hand tracking).
        const answeredHandClass = handToClassString(heroCards);
        if (answeredHandClass) {
          markHandResult(answeredHandClass, isCorrect);
        }

        // Record answer — pass heroCards so hand_combo/hand_class get populated
        recordBlastAnswer(isCorrect, heroCards);

        navigate('/blast-answer', {
          state: {
            hand: heroCards,
            chosenAction,
            correctAction: rangeEntry.correctAction,
            frequency: rangeEntry.frequency,
            allActions: rangeEntry.allActions,
            isCorrect,
            isLastQuestion,
            heroPosition: gameConfig.heroPosition,
            quizType: 'BLAST',
            // For Fixed Hands, pass the effective config (with current stack) so
            // the answer screen can display the right stack AND has availableStacks
            // for loading all blind-level circles.
            config: effectiveConfig,
            selectedHand: {
              card1: `${heroCards.card1Value}${heroCards.card1Suit}`,
              card2: `${heroCards.card2Value}${heroCards.card2Suit}`
            },
            // Pass raise sizes (sorted largest first) so answer screen can label
            // the correct action as "Large Raise" or "Small Raise"
            raiseSizes: [...raiseSizes].sort((a, b) => b - a),
            rangeHasAllin
          }
        });
        return;
      }

      // ── Cash-game legacy path (frequency-based) ────────────────────────────
      const chosenFrequency = parseInt(chosenAction.split('_')[0], 10);
      const result = await checkAnswer(
        { ...heroCards, heroPosition: gameConfig.heroPosition },
        chosenAction,
        gameConfig.range
      );

      const isLastQuestion = gameState.questionCount + 1 >= gameConfig.totalHands;
      navigate('/blast-answer', {
        state: {
          hand: heroCards,
          chosenFrequency,
          actualFrequency: result.actualFrequency,
          isCorrect: result.isCorrect,
          isLastQuestion,
          betType: gameConfig.betType,
          heroPosition: gameConfig.heroPosition,
          threeBetPosition: gameConfig.threeBetPosition,
          quizType: 'CASH',
          config: { ...gameConfig, range: gameConfig.range }
        }
      });
    } catch (error) {
      console.error('Error in handleAction:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [heroCards, gameConfig, effectiveConfig, rangeMap, checkAnswer, recordBlastAnswer, isProcessing, gameState, navigate, markHandResult, handToClassString, raiseSizes]);

  // ── Derive which non-raise actions are present in the loaded range ──────────
  const rangeHasCall = useMemo(() => {
    for (const e of rangeMap.values()) {
      if (e.action === 'call' || e.action === 'limp') return true;
    }
    return false;
  }, [rangeMap]);

  const rangeHasAllin = useMemo(() => {
    for (const e of rangeMap.values()) {
      if (e.action === 'allin') return true;
    }
    return false;
  }, [rangeMap]);

  const rangeHasRaise = useMemo(() => {
    for (const e of rangeMap.values()) {
      const a = e.action || '';
      if (a === 'raise' || a.startsWith('raise_')) return true;
    }
    return false;
  }, [rangeMap]);

  const rangeHasCheck = useMemo(() => {
    for (const e of rangeMap.values()) {
      if (e.action === 'check') return true;
    }
    return false;
  }, [rangeMap]);

  // ── Blast action buttons ──────────────────────────────────────────────────
  // freq=true  → adds blast-freq-button modifier (smaller font, wrapping text)
  // displayNode → optional JSX to render instead of label (label still used for action id)
  const mkBtn = useCallback((label, colorClass, freq = false, displayNode = null) => ({
    text: displayNode || label,
    buttonLargeClassName: `${colorClass}-button${freq ? ' blast-freq-button' : ''}${isProcessing ? ' opacity-50' : ''}`,
    onClick: () => handleAction(label)
  }), [isProcessing, handleAction]);

  // Build the actual decoy button for a chosen bucket ('jam' | 'raise' | 'call' | 'fold').
  // Label follows the same position-based conventions as the real buttons above:
  // SB's passive continue action is always 'Limp'; everyone else's is 'Check' when
  // there's no real Fold option (BB free-check spots) or 'Call' when facing a bet.
  const buildDecoyButton = useCallback((bucket, heroPos, noFold) => {
    const isSB = heroPos === 'SB';
    switch (bucket) {
      case 'jam':   return mkBtn('Jam', 'blast-jam');
      case 'raise': return mkBtn('Raise', 'blast-raise');
      case 'call':  return mkBtn(isSB ? 'Limp' : (noFold ? 'Check' : 'Call'), 'call');
      case 'fold':  return mkBtn('Fold', 'blast-fold');
      default:      return null;
    }
  }, [mkBtn]);

  // Builds a fixed, always-present button row from an ordered list of keys
  // (SB_OPENER_KEYS / BB_OPTION_KEYS_2SIZE / BB_OPTION_KEYS_1SIZE above) —
  // every key is rendered whether or not it's a real action at this stack, so
  // the row's length/order never varies. Real-vs-decoy grading is handled
  // entirely by isBlastAnswerCorrect elsewhere; this only builds the labels.
  const buildFixedButtonSet = useCallback((keys, heroPos) => {
    const callLabel = heroPos === 'SB' ? 'Limp' : 'Call';
    return keys.map((key) => {
      switch (key) {
        case 'jam':         return mkBtn('Jam', 'blast-jam');
        case 'raise_large': return mkBtn('Large Raise', 'blast-raise-large', true);
        case 'raise_small': return mkBtn('Small Raise', 'blast-raise', true);
        case 'raise':       return mkBtn('Raise', 'blast-raise');
        case 'limp':        return mkBtn(callLabel, 'call');
        case 'check':       return mkBtn('Check', 'call');
        case 'fold':        return mkBtn('Fold', 'blast-fold');
        default:            return null;
      }
    }).filter(Boolean);
  }, [mkBtn]);

  // If `btns` is exactly a binary choice, add one contextual decoy button (see
  // ACTION_HIERARCHY / pickDecoyBucket above) and re-sort so button order still
  // follows the project's Jam → Raise → Limp/Call → Fold priority rule.
  // `buckets` must be the same length/order as `btns`, one hierarchy bucket per button.
  const withDecoyIfBinary = useCallback((btns, buckets, heroPos, opts = {}) => {
    if (btns.length !== 2) return btns;
    const decoyBucket = pickDecoyBucket(buckets, heroPos, opts);
    if (!decoyBucket) return btns;
    const decoyButton = buildDecoyButton(decoyBucket, heroPos, !!opts.noFold);
    if (!decoyButton) return btns;

    const combined = buckets.map((bucket, i) => ({ bucket, btn: btns[i] }));
    combined.push({ bucket: decoyBucket, btn: decoyButton });
    combined.sort((a, b) => ACTION_HIERARCHY.indexOf(a.bucket) - ACTION_HIERARCHY.indexOf(b.bucket));
    return combined.map((c) => c.btn);
  }, [buildDecoyButton]);

  /**
   * Build binary action buttons from the loaded rangeMap.
   * Priority order: Jam → Large Raise → Small Raise → Raise → Limp → Check → Fold
   *
   * Shows only the actions that actually exist in the CSV data — no forced padding to 4.
   * Fold is added as a fallback UNLESS the hero has a free check (BB in option).
   *
   * @param {string} heroPos
   * @param {object} [opts]
   * @param {boolean} [opts.noFold=false] - suppress Fold (BB in option can always check free)
   */
  const getDynamicBinaryButtons = useCallback((heroPos, opts = {}) => {
    const { noFold = false } = opts;
    const isSB = heroPos === 'SB';

    // Step 1: Derive distinct action categories present in the range
    const derived = new Set();
    if (rangeHasAllin) derived.add('jam');
    if (rangeHasRaise) {
      // Large/Small only when there are 2+ distinct numeric raise sizes
      if (raiseSizes.length >= 2) {
        derived.add('raise_large');
        derived.add('raise_small');
      } else {
        derived.add('raise');
      }
    }
    if (rangeHasCall) derived.add('limp');
    if (rangeHasCheck) {
      // SB preflop: 'check' in the CSV means limp (SB can't check pre-flop).
      // BTN preflop: 'check' in the CSV means fold (BTN can't check pre-flop either).
      if (isSB) derived.add('limp');
      else if (heroPos !== 'BTN') derived.add('check');
      // For BTN, 'check' rows represent folding — Fold is already added below.
    }

    // Step 2: candidates = exactly what the data has, plus Fold when valid
    const candidates = new Set(derived);
    if (!noFold) candidates.add('fold');

    // Step 3: Pick in strict priority order (no cap — show all real options)
    const callLabel = isSB ? 'Limp' : 'Call';
    const PRIORITY = ['jam', 'raise_large', 'raise_small', 'raise', 'limp', 'check', 'fold'];
    const btns = [];
    const keys = [];

    const addBtn = (key) => {
      switch (key) {
        case 'jam':         btns.push(mkBtn('Jam', 'blast-jam')); break;
        case 'raise_large': btns.push(mkBtn('Large Raise', 'blast-raise-large', true)); break;
        case 'raise_small': btns.push(mkBtn('Small Raise', 'blast-raise', true)); break;
        case 'raise':       btns.push(mkBtn('Raise', 'blast-raise')); break;
        case 'limp':        btns.push(mkBtn(callLabel, 'call')); break;
        case 'check':       btns.push(mkBtn('Check', 'call')); break;
        case 'fold':        btns.push(mkBtn('Fold', 'blast-fold')); break;
        default: return;
      }
      keys.push(key);
    };

    for (const key of PRIORITY) {
      if (candidates.has(key)) addBtn(key);
    }

    // Only ever 2 real buttons at this point can trigger a decoy (see
    // withDecoyIfBinary above) — most stacks/positions naturally produce 3+.
    const buckets = keys.map(bucketOfKey);
    return withDecoyIfBinary(btns, buckets, heroPos, { noFold });
  }, [mkBtn, rangeHasAllin, rangeHasRaise, rangeHasCall, rangeHasCheck, raiseSizes, withDecoyIfBinary]);

  const getButtons = useCallback((config) => {
    if (!config) return [];

    const isBlast = config.quizType === 'BLAST' ||
                    config.format === '3way' || config.format === 'hu';

    if (isBlast) {
      const { action, subtype, villainAction } = config;
      const va = (villainAction || '').toLowerCase();
      const heroPos = config.heroPosition || config.position || '';

      if (action === 'open') {
        // Sub-range frequency mode: ask HOW OFTEN, not WHICH action
        // Button order: 100% → 50% → 0%
        if (subtype === 'raise') return [
          mkBtn('100%', 'blast-raise-large'),
          mkBtn('50%',  'blast-raise'),
          mkBtn('0%',   'blast-never', false, 'Never')
        ];
        if (subtype === 'jam' || subtype === 'allin') return [
          mkBtn('100%', 'blast-jam-hundred'),
          mkBtn('50%',  'blast-raise'),
          mkBtn('0%',   'blast-never', false, 'Never')
        ];
        if (subtype === 'call' || subtype === 'limp') {
          return [
            mkBtn('100%', 'blast-call-hundred'),
            mkBtn('50%',  'blast-limp-fifty'),
            mkBtn('0%',   'blast-never', false, 'Never')
          ];
        }
        // 'all' subtype — full open range.
        // SB Open always shows the fixed Jam/Raise/Limp/Fold set (see
        // SB_OPENER_KEYS above) — BTN Open keeps the old dynamic/decoy
        // behavior since BTN can have a real Large/Small raise split.
        if (heroPos === 'SB') return buildFixedButtonSet(SB_OPENER_KEYS, heroPos);
        return getDynamicBinaryButtons(heroPos);
      }

      // BB Checking range — frequency mode: how often does this hand check?
      // Button order: 100% → 50% → 0%
      if (action === 'check') return [
        mkBtn('100%', 'blast-call-hundred'),
        mkBtn('50%',  'blast-limp-fifty'),
        mkBtn('0%',   'blast-never', false, 'Never')
      ];

      // BB Raise range (vs SB limp) — handles BB Option sub-types
      if (action === 'raise') {
        // Specific raise size sub-range (freq mode) — e.g. subtype='raise_3'
        if (subtype && subtype.startsWith('raise_')) {
          return [
            mkBtn('100%', 'blast-raise-large'),
            mkBtn('50%',  'blast-raise'),
            mkBtn('0%',   'blast-never', false, 'Never')
          ];
        }
        // Jam-only sub-range (subtype='allin') — freq mode
        if (subtype === 'allin') return [
          mkBtn('100%', 'blast-jam-hundred'),
          mkBtn('50%',  'blast-raise'),
          mkBtn('0%',   'blast-never', false, 'Never')
        ];
        // Legacy freq modes
        if (subtype === 'raise-small' || subtype === 'raise-large') return [
          mkBtn('100%', 'blast-raise-large'),
          mkBtn('50%',  'blast-raise'),
          mkBtn('0%',   'blast-never', false, 'Never')
        ];
        // 'all' or 'raise-all' — full BB Option range (facing a limp): fixed
        // Jam/Large Raise/Small Raise/Check set (see BB_OPTION_KEYS_2SIZE
        // above). Falls back to a single Raise bucket at stacks that never
        // solve with two raise sizes (BB_OPTION_KEYS_1SIZE) instead of
        // faking a split. No Fold — BB can always check for free here.
        return buildFixedButtonSet(
          raiseSizes.length >= 2 ? BB_OPTION_KEYS_2SIZE : BB_OPTION_KEYS_1SIZE,
          heroPos
        );
      }

      if (action === 'complete') {
        return withDecoyIfBinary(
          [mkBtn('Complete', 'call'), mkBtn('Fold', 'blast-fold')],
          ['call', 'fold'],
          heroPos
        );
      }

      // Jam-range quiz (SB 3-bet jam over BTN open) — always frequency
      if (action === 'jam') return [
        mkBtn('100%', 'blast-jam-hundred'),
        mkBtn('50%',  'blast-raise'),
        mkBtn('0%',   'blast-never', false, 'Never')
      ];

      if (action === 'call') {
        // BB vs SB limp: dynamic buttons from rangeMap. BB can always check free (no fold).
        if (va === 'limp') {
          return getDynamicBinaryButtons(heroPos, { noFold: true });
        }
        // Calling frequency mode — 100% → 50% → 0%
        return [
          mkBtn('100%', 'blast-call-hundred'),
          mkBtn('50%',  'blast-limp-fifty'),
          mkBtn('0%',   'blast-never', false, 'Never')
        ];
      }

      // Fallback
      return withDecoyIfBinary(
        [mkBtn('Call', 'call'), mkBtn('Fold', 'blast-fold')],
        ['call', 'fold'],
        heroPos
      );
    }

    // ── Cash-game frequency buttons (original) ───────────────────────────────
    const createPctBtn = (pct, actionType, colorClass) => ({
      text: `${pct}%`,
      buttonLargeClassName: `${colorClass}-button${isProcessing ? ' opacity-50' : ''}`,
      onClick: () => handleAction(`${pct}_${actionType.toLowerCase()}`)
    });
    const pctGroup = (actionType) => [
      createPctBtn(100, actionType, 'hundred'),
      createPctBtn(50,  actionType, 'fifty'),
      createPctBtn(0,   actionType, 'zero')
    ];

    if (config.betType === 'SB_LIMP' || config.betType === 'LIMP') return pctGroup('Limp');
    switch (config.betType) {
      case BET_TYPES.OPEN:                                            return pctGroup('Open');
      case BET_TYPES.FACING_OPEN_CALL:
      case BET_TYPES.FACING_3BET_CALL:
      case BET_TYPES.FACING_4BET_CALL:                               return pctGroup('Call');
      case BET_TYPES.FACING_OPEN_3BET:                               return pctGroup('3Bet');
      case BET_TYPES.FACING_3BET_4BET:
      case BET_TYPES.SQUEEZE_4BET:                                   return pctGroup('4Bet');
      default:                                                        return pctGroup('Open');
    }
  }, [mkBtn, isProcessing, handleAction, raiseSizes, rangeHasCall, rangeHasAllin, rangeMap, getDynamicBinaryButtons, withDecoyIfBinary, buildFixedButtonSet]);

  const buttons = useMemo(() => getButtons(gameConfig), [gameConfig, getButtons]);

  const calculatedSeats = useMemo(() => {
    if (!gameConfig) return [];
    return calculateSeats(gameConfig);
  }, [gameConfig]);

  const questionText = useMemo(() => getQuestionText(effectiveConfig || gameConfig), [effectiveConfig, gameConfig]);
  const heroBetAndBlinds = useMemo(() => {
    if (!effectiveConfig) return { bet: 0, blinds: STARTING_STACK.toFixed(1) };
    return getHeroBetAndBlinds(effectiveConfig);
  }, [effectiveConfig]);

  let fixedBet = heroBetAndBlinds.bet;
  let fixedBlinds = heroBetAndBlinds.blinds;

  // Cash-game only: override bet for 3bet/4bet opener scenarios
  const isBlastGame = gameConfig?.quizType === 'BLAST' ||
                      gameConfig?.format === '3way' || gameConfig?.format === 'hu';
  if (!isBlastGame && gameConfig &&
      (gameConfig.betType === 'FACING_3BET_4BET' || gameConfig.betType === 'FACING_3BET_CALL') &&
      gameConfig.heroPosition === gameConfig.openPosition) {
    fixedBet = 2.5;
    fixedBlinds = (STARTING_STACK - 2.5).toFixed(1);
  }

  const currentHand = gameState.questionCount + 1;
  const totalHands = gameConfig.totalHands;
  const progress = (currentHand / totalHands) * 100;

  return (
    <div className="blast-table">
      {/* Cancel + title use the exact same font/size/color/spacing values as
          BlastConfigScreen/BlastRangeScreen's header, so they sit on the
          same Y-axis on every Blast screen. No divider line here — the
          progress bar below is this screen's stand-in for it, using the
          same 16px title-to-line/progress-bar gap as the divider line
          elsewhere (see BlastTable.css). */}
      <div className="blast-header-block">
        <div className="blast-header-nav-row">
          <a
            href="#"
            className="blast-header-cancel"
            onClick={(e) => {
              e.preventDefault();
              navigate('/blast-config');
            }}
          >
            Cancel
          </a>
        </div>

        <h1 className="blast-header-title">
          {gameConfig.format === 'hu' ? 'Blast - Heads Up' : 'Blast - 3 Way'}
        </h1>

        <div className="blast-progress">
          <div className="blast-progress-bar">
            <div
              className="blast-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="blast-hand-counter">
            {currentHand}/{totalHands}
          </div>
        </div>
      </div>

      <div className="blast-table-wrapper">
        <div className="blast-felt" />
        <RangeTitle gameConfig={effectiveConfig} />

        {seats.map(seat => (
          <Seat
            key={`${seat.id}-${gameState.questionCount}-${seat.title}`}
            {...seat}
            gameConfig={gameConfig}
          />
        ))}

        <div className="blast-hero-seat-wrapper">
          <HeroSeat
            id="hero"
            title={gameConfig.heroPosition}
            bet={fixedBet}
            blinds={fixedBlinds}
            card1Value={heroCards?.card1Value}
            card1Suit={heroCards?.card1Suit}
            card2Value={heroCards?.card2Value}
            card2Suit={heroCards?.card2Suit}
            betType={isBlastGame ? undefined : gameConfig.betType}
            openPosition={gameConfig.openPosition}
            isHeroOpener={gameConfig.heroPosition === gameConfig.openPosition}
            isDealer={isBlastGame && gameConfig.format === 'hu' && gameConfig.heroPosition === 'SB'}
            gameConfig={gameConfig}
          />
        </div>
      </div>

      <Question text={questionText} />
      <BottomControls buttons={buttons} />
    </div>
  );
};

export default React.memo(BlastTable);
